<template>
  <div class="viewer-wrapper">
    <header class="anim-view-header">
      <div class="home-btn control-btn" @click="handleHomeButton">
        <BaseIcon iconName="home"></BaseIcon>
      </div>

      <div class="header-content">
        <h1 class="header-1">
          <img src="@/assets/logotext.png" :alt="$t('app.logoAlt')" class="logo" />
        </h1>
      </div>
      <div class="header-actions">
        <div v-show="!isProcessing" class="controls">
        <div class="settings-container">
          <div
            class="settings-btn control-btn"
            :class="{ disabled: isLoading || hasError }"
            @click="handleOpenSettings"
          >
            <BaseIcon iconName="settings"> </BaseIcon>
          </div>

          <div v-if="showSettingsDropdown" @click="handleCloseSettings">
            <div class="modal-window" @click.stop>
              <div v-if="!isLoading" class="animation-details">
                <div class="modal-input">
                  <div class="modal-label">{{ $t('viewer.name') }}</div>
                  <BaseOutput
                    v-model="animationName"
                    :show-actions="true"
                    edit-icon="pencil"
                    @update:modelValue="saveAnimationName"
                  />
                </div>

                <div v-if="promt" class="modal-input">
                  <div class="modal-label">{{ $t('viewer.prompt') }}</div>
                  <div class="prompt">
                    <span>{{ promt }}</span>
                  </div>
                </div>

                <div class="modal-input">
                  <div class="modal-label">{{ $t('viewer.favorite') }}</div>
                  <div class="favorite-btn" @click.stop="toggleFavorite">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      :class="{ filled: isFavorite, outlined: !isFavorite }"
                    >
                      <path
                        d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                        :fill="isFavorite ? '#FFD700' : 'none'"
                        :stroke="isFavorite ? '#FFD700' : '#FFFFFF'"
                        stroke-width="1.5"
                      />
                    </svg>
                  </div>
                </div>

                <div class="modal-input">
                  <div class="modal-label">{{ $t('viewer.playbackSpeed') }}</div>
                  <div class="speed-slider">
                    <input
                      type="range"
                      min="0.1"
                      max="2"
                      step="0.1"
                      v-model="playbackSpeed"
                      @input="updatePlaybackSpeed"
                    />
                    <span class="modal-label speed-value">
                      {{ formattedPlaybackSpeed }}x
                    </span>
                    <button class="speed-reset" @click="resetPlaybackSpeed">{{ $t('viewer.reset') }}</button>
                  </div>
                </div>

                <div class="modal-input">
                  <div class="toggle-row">
                    <div class="modal-label toggle-label">{{ $t('viewer.inPlace') }}</div>
                    <ToggleComponent
                      :model-value="isInPlace"
                      :locked="isLoading || isProcessing"
                      size="compact"
                      @update:modelValue="handleInPlaceToggle"
                    />
                  </div>
                </div>

                <div class="modal-input">
                  <div class="modal-label">{{ $t('viewer.deleteAnimation') }}</div>
                  <button class="delete-btn" @click="showDeleteConfirm = true">
                    {{ $t('common.delete') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="download-container">
          <div
            class="control-btn"
            :class="{ disabled: isLoading || hasError }"
            @click="handleDownloadIconClick"
          >
            <BaseIcon iconName="download"></BaseIcon>
            <div v-if="isDownloading" class="loading-indicator"></div>
          </div>

          <div v-if="showFormatDropdown" @click="closeDropdown">
            <div class="modal-window" @click.stop>
              <div v-if="isDownloading">
                <div class="downloading-animation">
                  <span class="dot"></span>
                  <span class="dot"></span>
                  <span class="dot"></span>
                </div>
                <p>{{ $t('viewer.downloading') }}</p>
              </div>

              <div v-else>
                <div class="download-option">
                  <div class="toggle-row">
                    <div class="modal-label toggle-label">{{ $t('viewer.includeSkin') }}</div>
                    <ToggleComponent
                      v-model="includeSkin"
                      :locked="isDownloading"
                      size="compact"
                    />
                  </div>
                </div>
                <div class="modal-label">{{ $t('viewer.format') }}</div>
                <div class="format-buttons">
                  <div class="format-button-slot">
                    <div class="format-btn" @click="handleDownload('glb')">GLB</div>
                  </div>
                  <div class="format-button-slot">
                    <div class="format-btn" @click="handleDownload('bvh')">BVH</div>
                  </div>
                  <div class="format-button-slot">
                    <div class="format-btn" @click="handleDownload('fbx')">FBX</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </header>

    <!-- Blender Import Tip Popup -->
    <div v-if="showBlenderTip" class="blender-tip-overlay" @click="showBlenderTip = false">
      <div class="blender-tip-popup" @click.stop>
        <div class="blender-tip-header">{{ $t('viewer.blenderTip') }}</div>
        <p class="tip-text">
          {{ $t('viewer.blenderTipText') }}
        </p>
        <p class="tip-path">
          Import dialog &rarr; Armature &rarr; Bone Direction &rarr; Temperance
        </p>
        <div class="tip-actions">
          <div class="format-btn" @click="showBlenderTip = false">{{ $t('viewer.gotIt') }}</div>
        </div>
      </div>
    </div>

    <div v-if="hasError" class="error-container">
      <div class="error-content">
        <BaseIcon iconName="alert" class="error-icon" />
        <h2 class="error-title">{{ getErrorTitle }}</h2>
        <p class="error-message">{{ getErrorMessage }}</p>
        <PrimaryBtn @click="handleRetry">{{ $t('viewer.retry') }}</PrimaryBtn>
      </div>
    </div>

    <div v-if="isLoading || isProcessing" class="loading-container">
      <ProgressSpinner
        :progress="loadingProgress"
        :is-processing="showIndeterminateProcessing"
        :total-size="totalSize"
        :loaded-size="loadedSize"
        :show-size="!isProcessing && totalSize > 0"
        :label="loadingLabel"
        :subtitle="loadingSubtitle"
      />
    </div>

    <div v-show="!hasError && !isLoading && !isProcessing" class="model-viewer-container">
      <ThreeModelViewer
        ref="threeModelViewer"
        :animation-id="animationId"
        :task-id="taskId"
        :session-id="sessionId"
        @load-error="handleLoadError"
        @loading="handleModelLoading"
        @loaded="handleModelLoaded"
      />
    </div>

    <!-- Debug panel -->
    <div v-if="store.getters.isDebugMode" class="debug-panel" ref="panel">
      <h3>Debug Panel</h3>
      <div class="debug-controls">
        <button @click="handleSimulateLoading">
          Toggle Loading ({{ isLoading ? "On" : "Off" }})
        </button>
        <button @click="handleSimulateError('SERVER_ERROR')">
          Simulate Server Error
        </button>
        <button @click="handleSimulateError('PARSE_ERROR')">Simulate Parse Error</button>
        <button @click="handleSimulateError('NETWORK_ERROR')">
          Simulate Network Error
        </button>
        <button @click="handleResetState">Reset State</button>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteConfirm"
      class="confirmation-overlay"
      @click="showDeleteConfirm = false"
    >
      <div class="confirmation-modal" @click.stop>
        <h3>{{ $t('viewer.deleteAnimation') }}</h3>
        <p>
          {{ $t('viewer.deleteQuestion') }}
        </p>
        <div class="confirmation-actions">
          <button class="cancel-action" @click="showDeleteConfirm = false">{{ $t('common.cancel') }}</button>
          <button class="confirm-action" @click="handleDelete">{{ $t('common.delete') }}</button>
        </div>
      </div>
    </div>

    <!-- Notification Toast -->
    <NotificationToast
      :show="showNotification"
      :message="notificationMessage"
      :is-error="isErrorNotification"
      @close="closeNotification"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { simulateLoading, simulateError, resetState } from "@/utils/debugHelpers";
import { animationAPI } from "@/services/api";
import { trackEvent, trackError } from "@/utils/analytics";

import ThreeModelViewer from "@/components/ThreeModelViewer.vue";
import BaseIcon from "@/components/BaseComponents/BaseIcon.vue";
import BaseOutput from "@/components/BaseComponents/BaseOutput.vue";
import ProgressSpinner from "@/components/BaseComponents/ProgressSpinner.vue";
import PrimaryBtn from "@/components/BaseComponents/PrimaryBtn.vue";
import NotificationToast from "@/components/BaseComponents/NotificationToast.vue";
import ToggleComponent from "@/components/BaseComponents/ToggleComponent.vue";
import { useI18n } from 'vue-i18n';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const animationId = route.query.animationId;
const taskId =
  route.query.taskId ||
  (animationId
    ? window.sessionStorage.getItem(`pending-mocap-progress:${animationId}`)
    : null) ||
  null;
const sessionId = store.getters.getSessionId;

const threeModelViewer = ref(null);

const showFormatDropdown = ref(false);
const showSettingsDropdown = ref(false);
const showBlenderTip = ref(false);

const animationName = ref("");
const isFavorite = ref(false);
const promt = ref("");
const isInPlace = ref(false);
const includeSkin = ref(true);

const isDownloading = ref(false);

const isLoading = ref(true);
const loadingLabel = ref(t('viewer.loading.model'));
const loadingSubtitle = ref(t('viewer.loading.modelSubtitle'));

const playbackSpeed = ref(1);

const formattedPlaybackSpeed = computed(() => parseFloat(playbackSpeed.value).toFixed(1));

const hasError = ref(false);
const errorType = ref("");

const isProcessing = ref(false);
const loadingProgress = ref(0);
const totalSize = ref(0);
const loadedSize = ref(0);
const showDeterminateProcessingProgress = computed(
  () => isProcessing.value && totalSize.value > 0
);
const showIndeterminateProcessing = computed(
  () => isProcessing.value && !showDeterminateProcessingProgress.value
);

let processInterval = null;

const showDeleteConfirm = ref(false);

// Notification state
const showNotification = ref(false);
const notificationMessage = ref("");
const isErrorNotification = ref(false);
function applyDebugPreview() {
  if (!store.getters.isDebugMode) return;

  const mode = route.query.debug;
  const progress = Number(route.query.progress);

  if (mode === "loading") {
    isLoading.value = true;
    isProcessing.value = false;
    loadingProgress.value = Number.isFinite(progress) ? progress : 42;
    loadingLabel.value = t('viewer.loading.model');
    loadingSubtitle.value = t('viewer.loading.modelSubtitle');
  }

  if (mode === "processing") {
    isLoading.value = false;
    isProcessing.value = true;
    loadingProgress.value = Number.isFinite(progress) ? progress : 42;
    loadedSize.value = 0;
    totalSize.value = 0;
    loadingLabel.value = t('viewer.loading.processing');
    loadingSubtitle.value = t('viewer.loading.previewProcessingSubtitle');
  }
}

const getErrorTitle = computed(() => {
  switch (errorType.value) {
    case "SERVER_ERROR":
      return t('viewer.errors.serverTitle');
    case "PARSE_ERROR":
      return t('viewer.errors.parseTitle');
    case "NETWORK_ERROR":
      return t('viewer.errors.networkTitle');
    default:
      return t('viewer.errors.title');
  }
});

const getErrorMessage = computed(() => {
  switch (errorType.value) {
    case "SERVER_ERROR":
      return t('viewer.errors.server');
    case "PARSE_ERROR":
      return t('viewer.errors.parse');
    case "NETWORK_ERROR":
      return t('viewer.errors.network');
    default:
      return t('viewer.errors.unknown');
  }
});

function updatePlaybackSpeed() {
  threeModelViewer.value.setPlaybackSpeed(playbackSpeed.value);
}

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

function getDownloadAnalyticsContext(format) {
  return {
    animation_id: animationId,
    format,
    include_skin: includeSkin.value,
    in_place: isInPlace.value,
  };
}

async function handleDownload(format) {
  isDownloading.value = true;
  trackEvent("animation_download_started", getDownloadAnalyticsContext(format));

  try {
    const filename = animationName.value || "animation";

    if (format === "glb") {
      const arrayBuffer = await threeModelViewer.value.exportGLB({
        includeSkin: includeSkin.value,
      });
      const blob = new Blob([arrayBuffer], { type: "model/gltf-binary" });
      triggerBlobDownload(blob, `${filename}.glb`);
    } else if (format === "bvh") {
      const response = await animationAPI.downloadBVHAnimation(
        animationId,
        sessionId,
        isInPlace.value
      );
      if (!response.ok) throw new Error("Failed to download BVH file");
      const text = await response.text();
      const blob = new Blob([text], { type: "application/octet-stream" });
      triggerBlobDownload(blob, `${filename}.bvh`);
    } else if (format === "fbx") {
      const response = await animationAPI.downloadAnimation(
        animationId,
        sessionId,
        "fbx",
        isInPlace.value,
        includeSkin.value
      );
      if (!response.ok) throw new Error("Failed to download FBX file");
      const blob = await response.blob();
      triggerBlobDownload(blob, `${filename}.fbx`);
    }

    trackEvent("animation_download_completed", getDownloadAnalyticsContext(format));
  } catch (error) {
    console.error("Error downloading file:", error);
    trackEvent("animation_download_failed", getDownloadAnalyticsContext(format));
    trackError(
      "animation_download_error",
      error.message || "Unknown error",
      "animation_preview"
    );
    handleError(new Error(t('viewer.errors.failedDownload')));
  } finally {
    isDownloading.value = false;
    showFormatDropdown.value = false;
    if (format === "glb") {
      showBlenderTip.value = true;
    }
  }
}

async function handleInPlaceToggle(nextValue) {
  const previousValue = isInPlace.value;
  isInPlace.value = nextValue;

  const success = await threeModelViewer.value.reloadBVHAnimation({
    inPlace: nextValue,
  });

  if (!success) {
    isInPlace.value = previousValue;
  }
}

async function getAnimationInfo() {
  try {
    const data = await animationAPI.getAnimationDetails(sessionId, animationId, router);
    animationName.value = data.animation_name;
    isFavorite.value = data.is_favorite;
    promt.value = data.promt;
  } catch (error) {
    console.error("Error fetching animation info:", error);
  }
}

async function toggleFavorite() {
  try {
    const result = await animationAPI.toggleFavorite(sessionId, animationId);
    if (result.message) {
      isFavorite.value = !isFavorite.value;
    } else {
      console.error("Failed to update favorite status");
    }
  } catch (error) {
    console.error("Error setting favorite:", error);
  }
}

async function saveAnimationName(newName) {
  try {
    await animationAPI.changeAnimationName(sessionId, animationId, newName);
    showNotification.value = true;
    notificationMessage.value = t('viewer.errors.nameUpdated');
    isErrorNotification.value = false;
  } catch (error) {
    console.error("Error updating animation name:", error);
    showNotification.value = true;
    notificationMessage.value = t('viewer.errors.nameUpdateFailed', { error: error.message || t('common.unknownError') });
    isErrorNotification.value = true;
  }
}

function handleDownloadIconClick() {
  if (isLoading.value) return;
  showFormatDropdown.value = !showFormatDropdown.value;
}

function handleOpenSettings() {
  if (isLoading.value) return;
  showSettingsDropdown.value = !showSettingsDropdown.value;
}

function handleHomeButton() {
  router.push("/app/home");
}

function closeDropdown() {
  if (isDownloading.value) return;
  showFormatDropdown.value = false;
  showBlenderTip.value = false;
}

function handleCloseSettings() {
  showSettingsDropdown.value = false;
}

function handleOutsideClick(event) {
  const settingsDropdown = document.querySelector(".settings-container");
  const formatDropdown = document.querySelector(".download-container");

  if (
    showSettingsDropdown.value &&
    settingsDropdown &&
    !settingsDropdown.contains(event.target)
  ) {
    showSettingsDropdown.value = false;
  }

  if (
    showFormatDropdown.value &&
    formatDropdown &&
    !formatDropdown.contains(event.target)
  ) {
    showFormatDropdown.value = false;
  }
}

function handleError(error) {
  hasError.value = true;
  isLoading.value = false;

  if (error.message.includes("Server error")) {
    errorType.value = "SERVER_ERROR";
  } else if (error.message.includes("parse")) {
    errorType.value = "PARSE_ERROR";
  } else if (error.message.includes("download")) {
    errorType.value = "NETWORK_ERROR";
  } else {
    errorType.value = "UNKNOWN";
  }
}

function handleRetry() {
  hasError.value = false;
  errorType.value = "";
}

function handleSimulateLoading() {
  simulateLoading(isLoading, hasError, loadingProgress);
}

function handleSimulateError(type) {
  simulateError(hasError, isLoading, errorType, type);
}

function handleResetState() {
  resetState(hasError, isLoading, loadingProgress, errorType);
}

function resetPlaybackSpeed() {
  playbackSpeed.value = 1;
  updatePlaybackSpeed();
}

async function handleDelete() {
  try {
    await animationAPI.deleteAnimation(sessionId, animationId);
    showNotification.value = true;
    notificationMessage.value = t('viewer.errors.deleted');
    isErrorNotification.value = false;

    // Set timeout to redirect after showing success message
    setTimeout(() => {
      router.push("/app/animations");
    }, 1500);
  } catch (error) {
    console.error("Error deleting animation:", error);
    showNotification.value = true;
    notificationMessage.value = t('viewer.errors.deleteFailed', { error: error.message || t('common.unknownError') });
    isErrorNotification.value = true;
  } finally {
    showDeleteConfirm.value = false;
  }
}

function handleModelLoading({ progress, loaded, total, label, subtitle, processing }) {
  if (processing) {
    isProcessing.value = true;
    isLoading.value = false;
    loadingProgress.value = Number.isFinite(progress) ? progress : 0;
    loadedSize.value = Number.isFinite(loaded) ? loaded : 0;
    totalSize.value = Number.isFinite(total) ? total : 0;
    if (label) loadingLabel.value = label;
    if (subtitle) loadingSubtitle.value = subtitle;
    return;
  }
  isProcessing.value = false;
  isLoading.value = true;
  loadingProgress.value = progress || 0;
  loadedSize.value = loaded || 0;
  totalSize.value = total || 0;
  if (label) loadingLabel.value = label;
  if (subtitle) loadingSubtitle.value = subtitle;
}

function handleModelLoaded() {
  isLoading.value = false;
  isProcessing.value = false;
  loadingProgress.value = 0;
  loadedSize.value = 0;
  totalSize.value = 0;
  if (animationId) {
    window.sessionStorage.removeItem(`pending-mocap-progress:${animationId}`);
  }
}

function handleLoadError(message) {
  isLoading.value = false;
  isProcessing.value = false;
  loadingProgress.value = 0;
  loadedSize.value = 0;
  totalSize.value = 0;
  showNotification.value = true;
  notificationMessage.value = message || t('viewer.errors.failedLoad');
  isErrorNotification.value = true;
}

function closeNotification() {
  showNotification.value = false;
}

onMounted(() => {
  applyDebugPreview();
  document.addEventListener("click", handleOutsideClick);

  if (animationId) {
    getAnimationInfo();
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleOutsideClick);

  if (processInterval) {
    clearInterval(processInterval);
    processInterval = null;
  }
});
</script>

<style scoped>
.viewer-wrapper {
  width: 100%;
  height: 100vh;
}

.anim-view-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 10vh;
  position: relative;
  padding: 0 var(--spacing-xl);
  min-height: 60px;
}

.header-content {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--gap-md);
  width: auto;
  white-space: nowrap;
  z-index: 1;
}

.logo {
  height: 70px;
  width: auto;
  padding: 0 10px;
}

.header-1 {
  margin: 0;
  font-size: 1.5rem;
  line-height: 50px;
}

.control-btn {
  flex-shrink: 0;
  position: relative;
}

.header-controls {
  display: flex;
  gap: 0.5rem;
}

.settings-container,
.download-container {
  position: relative;
  display: inline-block;
}

.controls {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  z-index: 2;
}

.settings-btn {
  margin-right: var(--margin-sm);
}

.locked {
  cursor: not-allowed;
}

.modal-window {
  position: absolute;
  top: 110%;
  left: 50%;
  transform: translateX(-90%);
  background-color: var(--bg-cookie-modal);
  padding: var(--spacing-notification);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-dropdown);
  z-index: 999;
  min-width: 300px;
}

.modal-label {
  padding-bottom: var(--spacing-xl);
  color: var(--color-white-40);
  text-align: left;
}

.format-buttons {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--gap-lg);
}

.download-option {
  margin-bottom: var(--spacing-lg);
}

.format-button-slot {
  width: 100%;
}

.format-button-slot :deep(.hover-container) {
  display: block;
  width: 100%;
}

.format-button-slot :deep(.hover-text) {
  display: block;
  width: 100%;
}

.format-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--padding-btn-md);
  background-color: var(--bg-card);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-semibold);
  transition: var(--transition-normal);
  width: 100%;
  min-width: 0;
  text-align: center;
  box-sizing: border-box;
}

.format-btn:hover {
  background: var(--gradient-primary);
  color: var(--color-black);
}

.format-btn.disabled {
  opacity: 0.35;
  cursor: not-allowed;
  pointer-events: none;
  gap: var(--gap-xs);
}

.coming-soon-label {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-regular);
  opacity: 0.7;
}

.blender-tip-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.blender-tip-popup {
  background-color: var(--bg-cookie-modal);
  padding: var(--spacing-notification);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-dropdown);
  max-width: 420px;
  text-align: left;
}

.blender-tip-header {
  font-size: var(--font-size-header-alt);
  font-weight: var(--font-weight-semibold);
  margin-bottom: var(--spacing-md);
}

.tip-text {
  margin: 0 0 var(--spacing-sm) 0;
  font-size: var(--font-size-caption);
  line-height: 1.5;
  color: var(--color-white-70);
}

.tip-path {
  margin: 0 0 var(--spacing-md) 0;
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-semibold);
  color: var(--color-white);
  background-color: var(--bg-card);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius-sm);
}

.tip-actions {
  display: flex;
  justify-content: flex-end;
}

.animation-details {
  width: 25vw;
  min-width: 300px;
}

.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gap-lg);
}

.toggle-label {
  padding-bottom: 0;
}

.favorite-btn {
  cursor: pointer;
  text-align: left;
  margin-bottom: var(--padding-lg);
}

.prompt {
  text-align: left;
  padding: var(--padding-sm);
  background-color: var(--bg-card);
  border-radius: var(--border-radius-xs);
}

.progress-placeholder {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 20px 0;
}

.progress-circle {
  display: block;
}

.speed-slider {
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  gap: var(--gap-md);
  width: 100%;
}

.speed-slider input[type="range"] {
  flex: 1;
  min-width: 100px;
  -webkit-appearance: none;
  background: var(--bg-card);
  border-radius: var(--border-radius-xs);
  height: 4px;
}

.speed-slider input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border-radius: var(--border-radius-circle);
  background: var(--gradient-success);
  cursor: pointer;
}

.speed-slider input[type="range"]::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border: none;
  border-radius: var(--border-radius-circle);
  background: var(--gradient-success);
  cursor: pointer;
}

.speed-slider input[type="range"]::-ms-thumb {
  width: 16px;
  height: 16px;
  border: none;
  border-radius: var(--border-radius-circle);
  background: var(--gradient-success);
  cursor: pointer;
}

.speed-slider input[type="range"]:focus {
  outline: none;
}

.speed-value {
  padding: 0;
  min-width: 3ch;
  margin-right: var(--margin-sm);
  white-space: nowrap;
}

.speed-reset {
  background: none;
  border: none;
  color: var(--color-white-40);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: var(--border-radius-xs);
  font-size: var(--font-size-description);
  transition: var(--transition-normal);
  white-space: nowrap;
}

.speed-reset:hover {
  color: var(--color-white);
  background: var(--bg-card);
}

.modal-input:not(:last-child) {
  margin-bottom: 1.7rem;
}

.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.error-container,
.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(100vh - 30vh);
}

.error-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--spacing-xl);
  padding: var(--spacing-3xl);
  background: var(--color-white-05);
  border-radius: var(--border-radius-lg);
  max-width: 400px;
}

.error-icon {
  font-size: 2rem;
  color: var(--color-cancel);
}

.error-title {
  margin: 0;
  color: var(--color-white-90);
}

.error-message {
  margin: 0;
  color: var(--color-white-60);
  font-size: var(--font-size-body-sm);
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.downloading-animation {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin: var(--spacing-notification) 0;
}

.dot {
  width: 8px;
  height: 8px;
  background: var(--color-white);
  border-radius: var(--border-radius-circle);
  animation: jump 0.8s ease-in-out infinite;
}

.dot:nth-child(2) {
  animation-delay: var(--animation-delay-1);
}

.dot:nth-child(3) {
  animation-delay: var(--animation-delay-2);
}

@keyframes jump {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-10px);
  }
}

.loading-indicator {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  background-color: var(--color-cancel);
  border-radius: var(--border-radius-circle);
}

.debug-panel {
  position: fixed;
  background-color: var(--bg-cookie-consent);
  padding: var(--spacing-notification);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-dropdown);
  text-align: center;
  z-index: 999;
  top: 0;
  right: 50%;
}

.debug-controls {
  display: flex;
  flex-direction: column;
  gap: var(--gap-lg);
}

.debug-controls button {
  padding: var(--padding-btn-md);
  border: none;
  border-radius: var(--border-radius-xs);
  background: #2c3e50;
  color: var(--color-white);
  cursor: pointer;
}

.debug-controls button:hover {
  background: #34495e;
}

.model-viewer-container {
  box-sizing: border-box;
  padding: 0 var(--margin-sm);
  width: 99%;
  height: calc(100vh - 10vh);
}

.delete-btn {
  background: none;
  border: none;
  color: var(--color-cancel);
  padding: var(--padding-btn-md);
  border-radius: var(--border-radius-xs);
  cursor: pointer;
  font-size: var(--font-size-caption);
  transition: var(--transition-normal);
}

.delete-btn:hover {
  background: var(--color-cancel-15);
}

.confirmation-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-index-dropdown);
  backdrop-filter: var(--backdrop-blur-sm);
}

.confirmation-modal {
  background: var(--bg-cookie-modal);
  padding: var(--margin-lg);
  border-radius: var(--border-radius-xl);
  width: 90%;
  max-width: 400px;
  text-align: center;
}

.confirmation-modal h3 {
  color: var(--color-cancel);
  margin: 0 0 var(--spacing-xl) 0;
}

.confirmation-modal p {
  color: var(--color-white-70);
  margin: 0 0 var(--margin-lg) 0;
  line-height: var(--line-height-relaxed);
}

.confirmation-actions {
  display: flex;
  justify-content: center;
  gap: var(--spacing-xl);
}

.cancel-action,
.confirm-action {
  padding: var(--padding-btn-lg);
  border-radius: var(--border-radius-md);
  border: none;
  cursor: pointer;
  font-size: var(--font-size-caption);
  transition: var(--transition-normal);
}

.cancel-action {
  background: var(--bg-card);
  color: var(--color-white-70);
}

.cancel-action:hover {
  background: var(--color-white-20);
}

.confirm-action {
  background: var(--color-cancel);
  color: var(--color-white);
}

.confirm-action:hover {
  background: #ff6666;
}
</style>
